import { useMemo, useState } from 'react'
import { Sheet } from './Sheet'
import { Icon } from './Icon'
import { PixelPet } from '../identity/PixelPet'
import { Avatar } from '../chat/Avatar'

/**
 * The multi-select picker — search, chips of the selected, list, count-confirm.
 *
 * docs/UI-COMPONENTS.md §3.2: the original's #projInviteOverlay and
 * #projAttachOverlay are structurally identical (head + search + chips +
 * multi-select listbox + count + confirm), differing only in verb and data source.
 * This is the one implementation for both.
 */
export type PickerItem = {
  id: string
  label: string
  sub?: string
  /** Renders a pixel-pet avatar seeded by this name (agents); absent = initial tile. */
  pet?: string
  /** A person's Google photo. Present (even null) marks this row as a human and renders
   *  the Avatar — the photo when there is one, an initials tile when there isn't. */
  picture?: string | null
}

/**
 * The picker's BODY — search, chips of the selected, the single-column list — with
 * the selection owned by the caller.
 *
 * Split out of PickerSheet (owner, 2026-09-09: 把 + Project 下面列表 Agent 的方式改為
 * 這種單 Column, 並且後面顯示 server / machine … 與 Project Chat 裡面 "Attach Agent"
 * 的方式一樣). The ＋ sheet's Project tab had its own chip grid for the same job; two
 * renderers of "pick agents" is how the two drift. This is the one, and the sheet
 * below is the one that wraps it in a Cancel / confirm frame.
 */
export function PickerBody({
  placeholder,
  items,
  emptyText,
  selected,
  onToggle,
}: {
  placeholder: string
  items: PickerItem[]
  emptyText: string
  selected: ReadonlySet<string>
  onToggle: (id: string) => void
}) {
  const [q, setQ] = useState('')

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((i) => `${i.label} ${i.sub ?? ''}`.toLowerCase().includes(needle))
  }, [items, q])

  const picked = items.filter((i) => selected.has(i.id))

  return (
    <>
      <input
        className="nas-input"
        type="search"
        inputMode="search"
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />

      {picked.length > 0 && (
        <div className="pk-chips">
          {picked.map((i) => (
            <button
              key={i.id}
              type="button"
              className="pk-chip"
              onClick={() => onToggle(i.id)}
              title={`Remove ${i.label}`}
            >
              {i.label}
              <Icon name="close" size={11} />
            </button>
          ))}
        </div>
      )}

      <div className="pk-list" role="listbox" aria-multiselectable="true">
        {items.length === 0 && <div className="nas-note">{emptyText}</div>}
        {shown.map((i) => (
          <button
            key={i.id}
            type="button"
            role="option"
            aria-selected={selected.has(i.id)}
            className={`pk-row${selected.has(i.id) ? ' on' : ''}`}
            onClick={() => onToggle(i.id)}
          >
            {i.pet ? (
              <span className="pk-pet">
                <PixelPet name={i.pet} size={22} />
              </span>
            ) : i.picture !== undefined ? (
              <Avatar picture={i.picture} label={i.label} className="pk-face" />
            ) : (
              <span className="pk-initial">{(i.label[0] ?? '?').toUpperCase()}</span>
            )}
            <span className="pk-label">{i.label}</span>
            {i.sub && <span className="pk-sub">{i.sub}</span>}
            <Icon name={selected.has(i.id) ? 'check_box' : 'add'} size={15} />
          </button>
        ))}
      </div>
    </>
  )
}

export function PickerSheet({
  title,
  placeholder,
  items,
  confirmLabel,
  busy,
  emptyText,
  onConfirm,
  onClose,
}: {
  title: string
  placeholder: string
  items: PickerItem[]
  /** e.g. (n) => `Invite ${n}` — the count lives in the button, as the original does. */
  confirmLabel: (n: number) => string
  busy?: boolean
  emptyText: string
  onConfirm: (ids: string[]) => void
  onClose: () => void
}) {
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set())

  const toggle = (id: string) =>
    setSel((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Sheet
      title={title}
      onClose={onClose}
      // The list takes the room the search and the chips leave, and is the one thing that
      // scrolls (owner, 2026-10-28: 整個 list 下面有一些使用者會被擋住) — shell.css .sheet-fill.
      className="sheet-fill"
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="sheet-btn primary"
            disabled={sel.size === 0 || busy}
            onClick={() => onConfirm([...sel])}
          >
            {busy ? 'Working…' : confirmLabel(sel.size)}
          </button>
        </>
      }
    >
      <PickerBody
        placeholder={placeholder}
        items={items}
        emptyText={emptyText}
        selected={sel}
        onToggle={toggle}
      />
    </Sheet>
  )
}
